/**
 * TA 的性别与生日的表单控件：档案页的卡片（就地编辑）与新建宠物的第 04 项共用。
 * - 性别：只给“男孩 / 女孩”（用户 10-08：性别作为建宠必选；改回“不确定”后端拒 422，男孩女孩之间可以改，算纠错）。
 *   还没选过的（历史数据 unknown）两个都不按下。建宠表单把性别单独放在前面（sex={false}），这里就不再出性别这一组。
 * - 出生日期：年、月、日各一个下拉，都有“不知道”；日在选了月之后才能选，天数按那一年那一月算（不知道年份时 2 月到 29 日）；
 *   月改回“不知道”时日跟着清掉，不留下“有日没月”。“年份是估计的”只在选了年份时能勾，年份清掉时一起取消。
 *   “只知道大概几岁？”：选“约 N 岁”换算成出生年份并标为估计；岁数按年份现算，两个入口双向联动（2531 ADVISORY-R14 第 2 条）。
 *   只按 TA 家那边的今年换算；家的时区未知或认不出时不换算，只说“家的时区还没确定”，让主人直接选年份、勾“估计”（c84a 1745Z PET-FACT-01）。
 * - 为 TA 选定的生日：月、日两个下拉，默认“不选”；和出生日期分开记，不改出生日期（说明照 2531 的那句）。
 * - 合同第 1 节会被拒的组合在下面逐条说清（factsProblems），保存按钮由调用方按它停用。
 */
import { useId, useState } from "react";
import { ToggleChip } from "@/shared/ui";
import { APPROX_AGES, approxAgeLabel, approxAgeOf, daysInMonth, factsProblems, homeYear, latestYear, MIN_BIRTH_YEAR, problemText, SEX_CHOICES, SEX_TEXT, withApproxAge, type FactsForm } from "./factsModel";
import "./facts.css";

/** 能选的性别：只有男孩、女孩（SEX_CHOICES 里的“不确定”只留给读历史数据） */
const PICKABLE_SEX = SEX_CHOICES.filter((choice) => choice !== "unknown");
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const numberOrNull = (value: string) => (value === "" ? null : Number(value));
const dayOptions = (month: number | null, year: number | null, current: number | null) => {
  const max = month === null ? 31 : daysInMonth(month, year);
  const days = Array.from({ length: max }, (_, i) => i + 1);
  // 改了年或月以后原来的日子不存在了（例如 2021 年的 2 月 29 日）：照样显示着，下面说清要换一天，不偷偷改掉
  return current !== null && current > max ? [...days, current] : days;
};

export function FactsFields({
  form,
  onChange,
  disabled = false,
  now,
  homeTimeZone = null,
  sex = true,
}: {
  form: FactsForm;
  onChange: (next: FactsForm) => void;
  disabled?: boolean;
  now?: Date;
  /** TA 家的时区：“大概几岁”按那边的今年换算；拿不到或认不出时不换算，只提示直接选年份、勾“估计”（不借浏览器时区、不拿 UTC+14 猜） */
  homeTimeZone?: string | null;
  /** 要不要出性别这一组：建宠表单另起一项（必选），传 false */
  sex?: boolean;
}) {
  const id = useId();
  const thisYear = latestYear(now);
  const years = Array.from({ length: thisYear - MIN_BIRTH_YEAR + 1 }, (_, i) => thisYear - i);
  const set = (patch: Partial<FactsForm>) => onChange({ ...form, ...patch });
  const problems = factsProblems(form, now);
  const problemId = (problem: (typeof problems)[number]) => `${id}-problem-${problem}`;
  const birthProblems = problems.filter((problem) => !problem.startsWith("chosen_"));
  const chosenProblems = problems.filter((problem) => problem.startsWith("chosen_"));
  const yearProblems = birthProblems.filter((problem) => problem === "year_out_of_range" || problem === "estimate_needs_year" || problem === "date_invalid");
  const describe = (hint: string, issues: typeof problems) => [hint, ...issues.map(problemId)].join(" ");

  // “只知道大概几岁？”：点开后选“约 N 岁”，换算成出生年份并标为估计。岁数按年份现算、两边联动（改年份岁数跟着变）。
  // 年份本来就是估计的就直接开着；年月日都确定的不多占一格
  const approx = approxAgeOf(form, now, homeTimeZone);
  const byHome = homeYear(homeTimeZone, now) !== null;
  const [ageOpen, setAgeOpen] = useState(form.year !== null && form.estimated);
  return (
    <div className="ps-facts-fields">
      {sex ? (
        <fieldset className="ps-facts-group" disabled={disabled}>
          <legend>性别</legend>
          <div className="ps-segmented" role="group" aria-label="性别">
            {PICKABLE_SEX.map((choice) => (
              <ToggleChip key={choice} pressed={form.sex === choice} onToggle={() => set({ sex: choice })}>
                {SEX_TEXT[choice]}
              </ToggleChip>
            ))}
          </div>
        </fieldset>
      ) : null}

      <fieldset className="ps-facts-group" disabled={disabled}>
        <legend>出生日期</legend>
        <p id={`${id}-birth-hint`} className="ps-facts-hint">只知道一部分也可以，比如只知道年份，或者只知道几月几号。</p>
        <div className="ps-facts-date">
          <label htmlFor={`${id}-y`}>
            <span>年</span>
            <select id={`${id}-y`} className="ps-input" aria-invalid={yearProblems.length > 0} aria-describedby={describe(`${id}-birth-hint`, yearProblems)} value={form.year ?? ""} onChange={(e) => { const year = numberOrNull(e.target.value); set({ year, estimated: year === null ? false : form.estimated }); }}>
              <option value="">不知道</option>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <label htmlFor={`${id}-m`}>
            <span>月</span>
            <select id={`${id}-m`} className="ps-input" aria-invalid={birthProblems.length > 0} aria-describedby={describe(`${id}-birth-hint`, birthProblems)} value={form.month ?? ""} onChange={(e) => { const month = numberOrNull(e.target.value); set({ month, day: month === null ? null : form.day }); }}>
              <option value="">不知道</option>
              {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label htmlFor={`${id}-d`}>
            <span>日</span>
            <select id={`${id}-d`} className="ps-input" aria-invalid={birthProblems.length > 0} aria-describedby={describe(`${id}-birth-hint`, birthProblems)} value={form.day ?? ""} disabled={form.month === null} onChange={(e) => set({ day: numberOrNull(e.target.value) })}>
              <option value="">不知道</option>
              {dayOptions(form.month, form.year, form.day).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
        </div>
        <label className="ps-check ps-facts-check">
          <input type="checkbox" aria-invalid={problems.includes("estimate_needs_year")} aria-describedby={describe(`${id}-birth-hint`, problems.filter((problem) => problem === "estimate_needs_year"))} checked={form.year !== null && form.estimated} disabled={form.year === null} onChange={(e) => set({ estimated: e.target.checked })} />
          {/* 括号里的说明整体不拆（2531 2026-09-30：320 宽新建表单里会折成“……会显示为 /“约”）”）：放不下就在“（”前换行。
              用 nowrap 不用 wbr、行内块——量过 Chrome 在这两种写法下读屏名称会多出一个空格，nowrap 读出来仍是原来整句 */}
          <span>年份是估计的<span className="ps-facts-check__aside">（会显示为“约”）</span></span>
        </label>
        {ageOpen && !byHome ? (
          // 家的时区未知或认不出：不从岁数推年份（c84a 1745Z PET-FACT-01，不借浏览器时区、不拿 UTC+14 猜），让主人直接选年份、勾“估计”；
          // 已经填了的年份原样留着。两句各压在 320 宽新建表单一行放得下的字数里
          <p className="ps-facts-hint ps-facts-age__unknown" data-testid="facts-age-no-zone">
            家的时区还没确定，还不能按岁数换算；
            <br />
            直接选年份，勾上“年份是估计的”。
          </p>
        ) : ageOpen ? (
          <>
            <label htmlFor={`${id}-age`} className="ps-facts-age">
              <span>大概几岁</span>
              <select id={`${id}-age`} className="ps-input" value={approx ?? ""} aria-invalid={yearProblems.length > 0} aria-describedby={describe(`${id}-age-note`, yearProblems)} onChange={(e) => (e.target.value === "" ? set({ year: null, estimated: false }) : onChange(withApproxAge(form, Number(e.target.value), now, homeTimeZone)))}>
                <option value="">不知道</option>
                {APPROX_AGES.map((n) => <option key={n} value={n}>{approxAgeLabel(n)}</option>)}
              </select>
            </label>
            {/* 说的是两件事，在“；”后分两行（2531 2026-09-30：320 宽下整句会折出一个孤字“才算。”；量过卡片与新建表单两处、320/375 两个宽度，每句都各占一行） */}
            <p id={`${id}-age-note`} className="ps-facts-hint ps-facts-age__note">
              按 TA 家那边的今年换算，年份是约数；
              <br />
              点保存才算。
            </p>
          </>
        ) : (
          <button type="button" className="ps-facts-link" onClick={() => setAgeOpen(true)}>
            只知道大概几岁？
          </button>
        )}
      </fieldset>

      <fieldset className="ps-facts-group" disabled={disabled}>
        <legend>为 TA 选定的生日（可选）</legend>
        <p id={`${id}-chosen-hint`} className="ps-facts-hint">不知道 TA 哪天出生也没关系，可以给 TA 选一个过生日的日子，比如 TA 来到你身边的那天。</p>
        <div className="ps-facts-date ps-facts-date--two">
          <label htmlFor={`${id}-cm`}>
            <span>月</span>
            <select id={`${id}-cm`} className="ps-input" aria-invalid={chosenProblems.length > 0} aria-describedby={describe(`${id}-chosen-hint`, chosenProblems)} value={form.chosenMonth ?? ""} onChange={(e) => { const chosenMonth = numberOrNull(e.target.value); set({ chosenMonth, chosenDay: chosenMonth === null ? null : form.chosenDay }); }}>
              <option value="">不选</option>
              {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label htmlFor={`${id}-cd`}>
            <span>日</span>
            <select id={`${id}-cd`} className="ps-input" aria-invalid={chosenProblems.length > 0} aria-describedby={describe(`${id}-chosen-hint`, chosenProblems)} value={form.chosenDay ?? ""} disabled={form.chosenMonth === null} onChange={(e) => set({ chosenDay: numberOrNull(e.target.value) })}>
              <option value="">不选</option>
              {dayOptions(form.chosenMonth, null, form.chosenDay).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
        </div>
      </fieldset>

      <ul id={`${id}-problems`} className={problems.length ? "ps-facts-problems" : "visually-hidden"} role="status" aria-live="polite" aria-atomic="true">
        {problems.map((problem) => <li id={problemId(problem)} key={problem}>{problemText(problem, form)}</li>)}
      </ul>
    </div>
  );
}
